import { useCallback } from 'react';
import { defineMessage } from 'react-intl';

import { type MessageIds, useLocalize } from '@/hooks/use-localize';
import { PASSWORD_MIN_LENGTH, type PasswordPolicyViolation, validatePassword } from '@/utils/passwordPolicy';

// Localized requirement labels for the password-policy checklist. Declared here so
// formatjs extraction (`npm run i18n:extract`) picks them up; compiled into en-US.ts.
defineMessage({ id: 'auth.password.requirement.minLength', defaultMessage: 'At least {min} characters' });
defineMessage({ id: 'auth.password.requirement.uppercase', defaultMessage: 'At least one uppercase letter' });
defineMessage({ id: 'auth.password.requirement.lowercase', defaultMessage: 'At least one lowercase letter' });
defineMessage({ id: 'auth.password.requirement.number', defaultMessage: 'At least one number' });
defineMessage({ id: 'auth.password.requirement.special', defaultMessage: 'At least one special character' });

const REQUIREMENT_ORDER: readonly PasswordPolicyViolation[] = [
  'minLength',
  'uppercase',
  'lowercase',
  'number',
  'special',
];

const REQUIREMENT_MESSAGE_IDS: Record<PasswordPolicyViolation, MessageIds> = {
  minLength: 'auth.password.requirement.minLength',
  uppercase: 'auth.password.requirement.uppercase',
  lowercase: 'auth.password.requirement.lowercase',
  number: 'auth.password.requirement.number',
  special: 'auth.password.requirement.special',
};

/** A single password-policy rule with its localized label and whether the candidate satisfies it. */
export type PasswordRequirement = {
  code: PasswordPolicyViolation;
  label: string;
  met: boolean;
};

/**
 * Localizes the (pure) password policy into per-rule requirement labels for UI display.
 *
 * @returns Helpers to build the requirement checklist and to check overall validity.
 */
export function usePasswordPolicy() {
  const { tLocalize } = useLocalize();

  const getRequirements = useCallback(
    (password: string): PasswordRequirement[] => {
      const unmet = new Set(validatePassword(password));
      return REQUIREMENT_ORDER.map((code) => ({
        code,
        label: tLocalize(REQUIREMENT_MESSAGE_IDS[code], { min: PASSWORD_MIN_LENGTH }),
        met: !unmet.has(code),
      }));
    },
    [tLocalize],
  );

  const isPasswordValid = useCallback((password: string): boolean => validatePassword(password).length === 0, []);

  return { getRequirements, isPasswordValid };
}
